import { capitalize } from 'lodash';
import { CircleAlert, Plus } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AgreementFormEnum, EscrowPartyTypeEnum } from 'src/graphql/schema';

import { RepresentativesTable, RepresentativeModalActions, EmptyRepresentatives } from '.';

import { RepresentativeContextProvider, useRepresentative, type IRepresentativeContextProps } from '../model';

type Props = IRepresentativeContextProps & {
  suggestedChangesNode?: React.ReactNode;
};

const Container = ({ suggestedChangesNode }: { suggestedChangesNode?: React.ReactNode }) => {
  const { addRepresentative, role, currentPolicyRole, representatives, agreementType } = useRepresentative();

  let title = capitalize(role) + ' representatives';

  if (
    agreementType === AgreementFormEnum.Tripartite ||
    (agreementType === AgreementFormEnum.Bipartite && role === EscrowPartyTypeEnum.Depositor)
  )
    title += ' & signatories';

  return (
    <Card className="flex flex-col overflow-hidden">
      <CardHeader className="shrink-0 min-h-[110px]">
        <CardTitle className="flex items-center gap-2">
          {title}
          {!representatives.length && (
            <span className="text-orange-700 flex items-center gap-1">
              <CircleAlert className="size-3.5" />
              <i className="text-xs font-medium">Required</i>
            </span>
          )}
        </CardTitle>
        {agreementType === AgreementFormEnum.Bipartite && role === EscrowPartyTypeEnum.Beneficiary ? (
          <CardDescription>Who are the beneficiary key points of contact for this escrow?</CardDescription>
        ) : (
          <CardDescription>
            Who are the key points of contact for this escrow?
            <br />
            You must add at least one representative, and at least one must be designated as a signatory.
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="grow flex flex-col overflow-hidden">
        {suggestedChangesNode}
        {currentPolicyRole?.update && Boolean(representatives.length) && (
          <div className="flex justify-end mb-2 shrink-0">
            <Button variant="secondary" onClick={addRepresentative}>
              <Plus /> Add
            </Button>
          </div>
        )}
        {representatives.length ? <RepresentativesTable /> : <EmptyRepresentatives />}
      </CardContent>
    </Card>
  );
};

const RepresentativesContainer = ({ suggestedChangesNode, ...rest }: Props) => {
  return (
    <RepresentativeContextProvider {...rest}>
      <Container suggestedChangesNode={suggestedChangesNode} />
      <RepresentativeModalActions />
    </RepresentativeContextProvider>
  );
};

export default RepresentativesContainer;
